// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file AvatarStatusDot.tsx
 * @input Uses React, StyleX, theme tokens, and AvatarSizeContext
 * @output Exports AvatarStatusDot component and AvatarStatusDotProps type
 * @position Sub-component of Avatar; renders a size-aware status indicator
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/Avatar/Avatar.doc.mjs (features, files table)
 * - /packages/core/src/Avatar/index.ts (exports)
 * - /apps/storybook/stories/Avatar.stories.tsx (storybook stories)
 * - /packages/cli/templates/blocks/components/Avatar/ (showcase blocks)
 */
import React, { use } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, radiusVars } from "../theme/tokens.stylex.js";
import { AvatarSizeContext } from "./AvatarSizeContext.js";
import { mergeProps } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";

/**
 * Resolves the status dot size, border width, and icon size based on the
 * avatar size.
 *
 * Uses discrete size tiers rather than a continuous ratio so the dot
 * looks intentional at every avatar size:
 *
 *   | Avatar size  | Dot  | Border | Icon |
 *   |--------------|------|--------|------|
 *   | ≤ 36px       | 10px | 1px    | —    |
 *   | 40–72px      | 20px | 2px    | 12px |
 *   | ≥ 96px       | 32px | 4px    | 18px |
 *
 * Icons are not rendered at the smallest tier — there isn't enough
 * room for them to be legible.
 */
import { jsx as _jsx } from "react/jsx-runtime";
function resolveStatusDotSize(avatarSize) {
  if (avatarSize <= 36) {
    return {
      dotSize: 10,
      borderWidth: 1,
      iconSize: 0
    };
  }
  if (avatarSize <= 72) {
    return {
      dotSize: 20,
      borderWidth: 2,
      iconSize: 12
    };
  }
  return {
    dotSize: 32,
    borderWidth: 4,
    iconSize: 18
  };
}

/**
 * Extensible variant map for AvatarStatusDot.
 *
 * Theme packages can add custom variants via TypeScript module augmentation:
 * @example
 * ```
 * declare module '@astryxdesign/core/Avatar' {
 *   interface AvatarStatusDotVariantMap {
 *     'away': true;
 *   }
 * }
 * ```
 */

/**
 * AvatarStatusDot variant type. Extensible via module augmentation of AvatarStatusDotVariantMap.
 */

const styles = {
  dot: {
    kaIpWk: "xjspbzw",
    ksu8eU: "x1y0btm7",
    kVAM5u: "x1touxvs",
    kB7OPa: "x9f619",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    $$css: true
  },
  success: {
    kWkggS: "xdsz4j9",
    $$css: true
  },
  neutral: {
    kWkggS: "xdomwnj",
    $$css: true
  },
  error: {
    kWkggS: "x1pjz0fi",
    $$css: true
  },
  icon: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kMwMTN: "xrkvqaz",
    kLWn49: "x14ju556",
    $$css: true
  }
};
const dynamicStyles = {
  size: (dotSize, borderWidth) => [{
    kzqmXN: dotSize != null ? "x5lhr3w" : dotSize,
    kZKoxP: dotSize != null ? "x16ye13r" : dotSize,
    kMzoRj: borderWidth != null ? "x1mw0n95" : borderWidth,
    $$css: true
  }, {
    "--x-width": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(dotSize),
    "--x-height": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(dotSize),
    "--x-borderWidth": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(borderWidth)
  }],
  iconSize: size => [{
    kzqmXN: size != null ? "x5lhr3w" : size,
    kZKoxP: size != null ? "x16ye13r" : size,
    $$css: true
  }, {
    "--x-width": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(size),
    "--x-height": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(size)
  }]
};
const variantStyleMap = {
  success: styles.success,
  neutral: styles.neutral,
  error: styles.error
};

/**
 * A status indicator dot that automatically scales to match the parent
 * Avatar's size.
 *
 * Must be used inside an Avatar's `status` prop so it can read
 * the avatar size from context.
 *
 * @example
 * ```
 * <Avatar
 *   name="John Doe"
 *   size="medium"
 *   status={<AvatarStatusDot variant="success" label="Online" />}
 * />
 * <Avatar
 *   name="Jane Smith"
 *   size="large"
 *   status={<AvatarStatusDot variant="success" label="Verified" icon={<CheckIcon />} />}
 * />
 * ```
 */
export function AvatarStatusDot({
  ref,
  variant = 'success',
  label,
  icon,
  xstyle,
  className,
  style,
  ...props
}) {
  const avatarSize = use(AvatarSizeContext);
  const {
    dotSize,
    borderWidth,
    iconSize
  } = resolveStatusDotSize(avatarSize);
  return /*#__PURE__*/_jsx("div", {
    ...props,
    ref: ref,
    ...(label ? {
      role: 'img',
      'aria-label': label
    } : undefined),
    ...mergeProps(themeProps('avatar-status-dot', {
      variant
    }), stylex.props(styles.dot, variantStyleMap[variant], dynamicStyles.size(dotSize, borderWidth), xstyle), className, style),
    children: icon && iconSize > 0 && /*#__PURE__*/_jsx("span", {
      "aria-hidden": "true",
      ...stylex.props(styles.icon, dynamicStyles.iconSize(iconSize)),
      children: icon
    })
  });
}
AvatarStatusDot.displayName = 'AvatarStatusDot';